import { useEffect, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Landmark } from "lucide-react";
import { usePinPrompt } from "@/components/app/PinPrompt";

export default function Bank() {
  const pinPrompt = usePinPrompt();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", account_number: "", ifsc: "", branch: "", upi_id: "", opening_balance: 0 });

  const load = async () => setRows((await api.get("/banks")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.name) { toast.error("Bank name required"); return; }
    await pinPrompt();
    try { await api.post("/banks", f); toast.success("Bank added"); setOpen(false); setF({ name: "", account_number: "", ifsc: "", branch: "", upi_id: "", opening_balance: 0 }); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await pinPrompt();
    try { await api.delete(`/banks/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 gap-3">
        <div>
          <div className="overline">Banking</div>
          <h1 className="text-3xl font-bold tracking-tight">Bank Accounts</h1>
        </div>
        <Button className="btn-primary" onClick={() => setOpen(true)} data-testid="bank-add"><Plus size={14} /> Add Bank</Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((b) => (
          <div key={b._id_str} className="bg-white border border-border rounded-lg p-4 kpi-card" data-testid={`bank-card-${b._id_str}`}>
            <div className="flex items-center gap-2"><Landmark size={16} className="text-[#064E3B]" /><span className="overline">{b.branch || "Bank"}</span></div>
            <div className="text-xl font-bold mt-1">{b.name}</div>
            <div className="text-xs text-slate-500 font-mono">A/c {b.account_number || "—"} · IFSC {b.ifsc || "—"}</div>
            {b.upi_id && <div className="text-xs font-mono text-slate-500">UPI: {b.upi_id}</div>}
            <div className="mt-3 flex items-center justify-between">
              <div>
                <div className="overline">Balance</div>
                <div className="text-2xl font-mono font-bold text-[#064E3B]">{fmtINR(b.balance)}</div>
              </div>
              <button className="text-red-600 hover:bg-red-50 p-1 rounded" onClick={() => del(b._id_str)}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="col-span-3 text-sm text-muted-foreground p-6">No banks added.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Bank</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Bank Name*</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="bank-form-name" /></div>
            <div><Label>Branch</Label><Input value={f.branch} onChange={(e) => setF({ ...f, branch: e.target.value })} /></div>
            <div><Label>Account Number</Label><Input value={f.account_number} onChange={(e) => setF({ ...f, account_number: e.target.value })} /></div>
            <div><Label>IFSC</Label><Input value={f.ifsc} onChange={(e) => setF({ ...f, ifsc: e.target.value })} /></div>
            <div><Label>UPI ID</Label><Input value={f.upi_id} onChange={(e) => setF({ ...f, upi_id: e.target.value })} /></div>
            <div><Label>Opening Balance</Label><Input type="number" value={f.opening_balance} onChange={(e) => setF({ ...f, opening_balance: +e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={save} data-testid="bank-form-save">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
